import { useQuery } from '@tanstack/react-query'
import { jobsApi } from '../lib/api'
import type { JobStatusDto } from '../types/job'

const TERMINAL: ReadonlySet<JobStatusDto['status']> = new Set(['Succeeded', 'Failed'])

export interface UseJobPollingOptions {
  /** Poll interval in ms while the job is Pending/Running. Default 1500ms. */
  intervalMs?: number
}

/**
 * Polls GET /api/jobs/{id} (slice 6b worker tier, WI-41/WI-42) until the job reaches a
 * terminal state (Succeeded or Failed). Polling also stops on a request error — notably a
 * 404, which the backend returns for a job the caller doesn't own or that doesn't exist
 * (never treat 404 as "keep retrying").
 */
export function useJobPolling(jobId: string | null | undefined, options?: UseJobPollingOptions) {
  const intervalMs = options?.intervalMs ?? 1500

  const query = useQuery<JobStatusDto>({
    queryKey: ['jobs', jobId],
    queryFn: () => jobsApi.getStatus(jobId as string).then((r) => r.data),
    enabled: !!jobId,
    retry: false,
    // v5 changed this callback's signature from (data, query) to (query) alone — data now comes
    // off query.state.data. The decision logic itself (terminal-status check, then error check,
    // else keep polling at intervalMs) is unchanged.
    refetchInterval: (query) => {
      const data = query.state.data
      if (data && TERMINAL.has(data.status)) return false
      if (query.state.error) return false // e.g. 404 — never retry-poll forever on a hard failure
      return intervalMs
    },
  })

  const isTerminal = (!!query.data && TERMINAL.has(query.data.status)) || query.isError
  const isError = query.data?.status === 'Failed' || query.isError

  return { ...query, isTerminal, isError }
}
